import * as React from "react";
import {
  LineChart as RechartsLineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { ChartContainer, ChartTooltip, ChartTooltipContent, ChartLegend, ChartLegendContent } from "./chart";
import { cn } from "@/lib/utils";

export interface LineChartProps {
  data: any[];
  className?: string;
  config?: any;
  variant?: "default" | "animated" | "interactive" | "minimal" | "curved" | "stepped" | "dashed";
  theme?: "light" | "dark" | "minimal";
  responsive?: boolean;
  accessible?: boolean;
  dataKeys?: string[];
  colors?: string[];
  strokeWidth?: number;
  showGrid?: boolean;
  showLegend?: boolean;
  showTooltip?: boolean;
  showDots?: boolean;
  showActiveDot?: boolean;
  dotRadius?: number;
  activeDotRadius?: number;
  height?: number | string;
  width?: number | string;
  type?: "monotone" | "linear" | "step" | "stepBefore" | "stepAfter" | "basis" | "basisOpen" | "basisClosed" | "natural" | "monotoneX" | "monotoneY";
}

export function LineChart({
  data,
  className,
  config = {},
  variant = "default",
  theme = "light",
  responsive = true,
  accessible = true,
  dataKeys = ["value"],
  colors = [
    "hsl(221.2 83.2% 53.3%)",
    "hsl(217.2 91.2% 59.8%)",
    "hsl(214.3 31.8% 91.4%)",
    "hsl(210 40% 96%)",
  ],
  strokeWidth = 2,
  showGrid = true,
  showLegend = false,
  showTooltip = true,
  showDots = true,
  showActiveDot = true,
  dotRadius = 4,
  activeDotRadius = 6,
  height,
  width,
  type = "monotone",
  ...props
}: LineChartProps) {
  // Determine colors based on theme
  const getThemeColors = () => {
    switch (theme) {
      case "dark":
        return [
          "#60a5fa",
          "#34d399",
          "#fbbf24",
          "#f87171",
        ];
      case "minimal":
        return [
          "#6b7280",
          "#9ca3af",
          "#d1d5db",
          "#e5e7eb",
        ];
      default:
        return colors;
    }
  };

  const themeColors = getThemeColors();

  // Determine variant-specific props
  const getVariantProps = () => {
    switch (variant) {
      case "animated":
        return {
          className: cn(className, "wl-line-chart-animated"),
          animationDuration: 1500,
          animationEasing: "ease-out",
        };
      case "interactive":
        return {
          className: cn(className, "wl-line-chart-interactive"),
        };
      case "minimal":
        return {
          className: cn(className, "wl-line-chart-minimal"),
          strokeWidth: 1,
          showDots: false,
        };
      case "curved":
        return {
          className: cn(className, "wl-line-chart-curved"),
          type: "natural",
          strokeWidth: 3,
        };
      case "stepped":
        return {
          className: cn(className, "wl-line-chart-stepped"),
          type: "step",
        };
      case "dashed":
        return {
          className: cn(className, "wl-line-chart-dashed"),
          strokeDasharray: "5 5",
        };
      default:
        return {
          className,
        };
    }
  };

  const variantProps = getVariantProps();

  // Get line type based on variant
  const getLineType = () => {
    if (variant === "curved") return "natural";
    if (variant === "stepped") return "step";
    return type;
  };

  return (
    <div
      className={cn(
        "wl-line-chart",
        "wl-line-chart-theme",
        `wl-line-chart-theme--${theme}`,
        variant !== "default" && `wl-line-chart-${variant}`,
        responsive && "wl-line-chart-responsive",
        accessible && "wl-line-chart-accessible",
        className
      )}
      data-chart-type="line"
      style={{ height, width }}
      {...props}
    >
      <ChartContainer config={config}>
        <RechartsLineChart
          data={data}
          margin={{ left: 20, right: 20, top: 20, bottom: 20 }}
          {...variantProps}
        >
          <XAxis
            dataKey="name"
            stroke="#888888"
            fontSize={12}
            tickLine={false}
            axisLine={false}
          />
          <YAxis
            stroke="#888888"
            fontSize={12}
            tickLine={false}
            axisLine={false}
          />
          
          {showGrid && (
            <CartesianGrid
              strokeDasharray="3 3"
              stroke="rgba(0,0,0,0.1)"
            />
          )}
          
          {showTooltip && (
            <ChartTooltip
              content={<ChartTooltipContent />}
              cursor={false}
            />
          )}
          
          {showLegend && (
            <ChartLegend content={<ChartLegendContent />} />
          )}
          
          {dataKeys.map((dataKey, index) => (
            <Line
              key={dataKey}
              type={getLineType()}
              dataKey={dataKey}
              stroke={themeColors[index % themeColors.length]}
              strokeWidth={variant === "minimal" ? 1 : strokeWidth}
              strokeDasharray={variant === "dashed" ? "5 5" : undefined}
              dot={
                showDots
                  ? {
                      fill: themeColors[index % themeColors.length],
                      strokeWidth: 2,
                      r: dotRadius,
                      stroke: "white",
                    }
                  : false
              }
              activeDot={
                showActiveDot
                  ? {
                      r: activeDotRadius,
                      strokeWidth: 2,
                      stroke: "white",
                      fill: themeColors[index % themeColors.length],
                    }
                  : false
              }
              animationDuration={variant === "animated" ? 1500 : undefined}
              animationEasing={variant === "animated" ? "ease-out" : undefined}
            />
          ))}
        </RechartsLineChart>
      </ChartContainer>
    </div>
  );
}

// Export for convenience
export { LineChart as default }; 